import { webglCensusSnapshot } from './webglCensus';
import { track, onPerfMark } from '../analytics';

/**
 * Instrumentacao leve do caminho critico do 3D.
 *
 * Usa a User Timing API nativa (`performance.mark` / `performance.measure`),
 * entao os marcos aparecem na aba Performance do DevTools e tambem podem ser
 * lidos por script (`window.__turbineMarks()`), o que e o unico caminho viavel
 * quando o customizador roda dentro do iframe da loja num aparelho real.
 *
 * Custo: uma chamada nativa por marco (dezenas por sessao). Nao ha timers,
 * nao ha loops, nao ha envio de rede.
 */

const PREFIX = 'turbine:';

export type PerfMarkName =
  | 'app-boot'
  | 'react-mount'
  | 'customizer-payload-start'
  | 'customizer-payload-ready'
  | 'viewer-mount'
  | 'renderer-init'
  | 'environment-ready'
  | 'glb-request-start'
  | 'glb-loaded'
  | 'models-attached'
  | 'first-frame'
  | 'scene-settled'
  | 'viewer-interactive'
  | 'ar-prewarm-start'
  | 'ar-prewarm-done'
  // ── Fatias da janela entre "da para usar" e "veu sai" ──
  // Medido no Galaxy S21: 2097ms entre viewer-interactive e scene-settled. As
  // duas metades possiveis dessa janela pedem correcoes OPOSTAS:
  //
  //   se for espera pelos COMPONENTES da segunda rodada (tampa/capa/anel),
  //   antecipar o download nao expoe nada — e ganho sem custo visual;
  //
  //   se for aplicacao de COR/TEXTURA/DECAL, revelar antes mostra o material
  //   cru e a peca mudando na frente do cliente — ai e troca, nao conserto.
  //
  // Sem separar as duas, mexer no criterio de revelacao e chute.
  | 'components-request-start'
  | 'components-loaded'
  | 'variant-applied'
  | 'decals-applied';

const seen = new Set<string>();

/** Marcos que viram evento de analytics (uma vez por documento). */
const ANALYTICS_MARKS: Partial<Record<PerfMarkName, string>> = {
  'viewer-interactive': 'viewer_ready',
};

/** Marca um instante. Marcos repetidos sao ignorados (queremos o primeiro). */
export function mark(name: PerfMarkName): void {
  if (typeof performance === 'undefined' || !performance.mark) return;
  const key = PREFIX + name;
  if (seen.has(key)) return;
  seen.add(key);
  try {
    performance.mark(key);
  } catch {
    // User Timing indisponivel — instrumentacao e opcional.
  }
  onPerfMark(name);
  const evento = ANALYTICS_MARKS[name];
  if (evento) track(evento, { duration_ms: performance.now() });
}

/** Mede o intervalo entre dois marcos ja registrados. */
export function measure(name: string, from: PerfMarkName, to: PerfMarkName): void {
  if (typeof performance === 'undefined' || !performance.measure) return;
  const a = PREFIX + from;
  const b = PREFIX + to;
  if (!seen.has(a) || !seen.has(b)) return;
  try {
    performance.measure(PREFIX + name, a, b);
  } catch {
    // marcos removidos por buffer cheio — ignora.
  }
}

interface MarkReport {
  name: string;
  since: number;
}

/** Tabela de marcos relativa ao inicio da navegacao, em ms. */
export function collectMarks(): MarkReport[] {
  if (typeof performance === 'undefined' || !performance.getEntriesByType) return [];
  return performance
    .getEntriesByType('mark')
    .filter(e => e.name.startsWith(PREFIX))
    .map(e => ({ name: e.name.slice(PREFIX.length), since: Math.round(e.startTime) }))
    .sort((a, b) => a.since - b.since);
}

if (typeof window !== 'undefined') {
  (window as unknown as { __turbineMarks?: () => MarkReport[] }).__turbineMarks = collectMarks;
}

const CHAVE_RELATORIO = 'turbine_perf_report';

/**
 * O relatorio esta ligado?
 *
 * `?perfreport=1` liga e GRAVA em localStorage; `?perfreport=0` desliga. A
 * gravacao existe porque o parametro nao chega ate aqui pelo caminho real: a
 * extensao do tema monta a URL do iframe do ZERO (new URLSearchParams) e so
 * repassa `turbine-sandbox` e `variant`. Sem persistir, medir pelo fluxo da loja
 * exigiria alterar a extensao, o que demanda `shopify app deploy`.
 *
 * Como o iframe roda na MESMA origem do customizador, abrir a URL direta uma vez
 * com ?perfreport=1 deixa o relatorio ligado tambem quando a pagina e aberta
 * pela loja. Mesmo padrao que o turbine-sandbox usa.
 */
function relatorioLigado(): boolean {
  try {
    // No SANDBOX o relatorio e sempre ligado.
    //
    // O sinalizador em localStorage exigia que o passo de ativacao fosse feito
    // NAQUELE navegador especifico. Na pratica isso significou tres rodadas de
    // medicao perdidas: o usuario testava num aparelho e o sinalizador estava
    // em outro, e a ausencia de relato era indistinguivel de nao aconteceu.
    // Sandbox e ambiente de teste — um beacon por carregamento aqui e o preco
    // certo por medicao confiavel. Producao segue inalterada: la continua
    // valendo o parametro e o armazenamento local, e sem eles nao ha envio.
    if (/(^|\.)sandbox\./.test(window.location.hostname)) return true;
    const p = new URLSearchParams(window.location.search).get('perfreport');
    if (p === '1') { window.localStorage.setItem(CHAVE_RELATORIO, '1'); return true; }
    if (p === '0') { window.localStorage.removeItem(CHAVE_RELATORIO); return false; }
    return window.localStorage.getItem(CHAVE_RELATORIO) === '1';
  } catch {
    // localStorage bloqueado: cai no parametro da URL apenas
    try { return new URLSearchParams(window.location.search).get('perfreport') === '1'; }
    catch { return false; }
  }
}

/**
 * Resumo do censo WebGL deste realm, pequeno o suficiente para viajar em todo
 * relato (o endpoint aceita 16kb). Devolve `null` se o censo nao estiver
 * disponivel — telemetria nunca pode quebrar a pagina.
 */
function censoResumido(): Record<string, unknown> | null {
  try {
    const c = webglCensusSnapshot();
    return {
      realm: c.realm,
      vivos: c.renderers.length,
      criados: c.renderersCreated,
      dispostos: c.renderersDisposed,
      canvas: c.canvasesInDocument,
      iframes: c.iframesInDocument,
      modelViewers: c.modelViewersInDocument,
      perdidos: c.contextLostEvents,
      geo: c.renderers.reduce((a, r) => a + (r.memory?.geometries || 0), 0),
      tex: c.renderers.reduce((a, r) => a + (r.memory?.textures || 0), 0),
      progs: c.renderers.reduce((a, r) => a + (r.programs || 0), 0),
    };
  } catch {
    return null;
  }
}

/**
 * Envia os marcos ao endpoint de sonda para que sejam lidos NO SERVIDOR.
 *
 * Por que existe: medir o caminho critico exige aparelho real, e o unico canal
 * que restou e o proprio comprador abrindo a pagina. O dispositivo remoto que eu
 * usava ficou indisponivel ("Automate testing time expired"), e ler as marcas
 * pelo console nao e viavel num iPhone dentro do iframe da loja.
 *
 * So dispara com ?perfreport=1 na URL — nao ha envio em uso normal. Uma
 * requisicao por carregamento, com sendBeacon (nao atrasa a pagina, sobrevive a
 * navegacao) e queda para fetch keepalive.
 */
export function reportarMarcas(rotulo: string, anexo?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  try {
    if (!relatorioLigado()) return;
    const marcas: Record<string, number> = {};
    for (const e of performance.getEntriesByType('mark')) {
      const nome = e.name.startsWith(PREFIX) ? e.name.slice(PREFIX.length) : e.name;
      if (!(nome in marcas)) marcas[nome] = Math.round(e.startTime);
    }
    // NAO retorna sem marcas.
    //
    // O retorno antecipado que existia aqui silenciava exatamente o caso mais
    // grave: quando o carregamento trava tao cedo que nem as marcas iniciais
    // foram gravadas, nao havia relato nenhum — e "travou sem deixar rastro" e
    // indistinguivel de "nao aconteceu". Foi o que se viu na Galeria travada na
    // tela de carregamento: o servidor respondeu o payload, e do cliente nao
    // chegou uma linha.
    //
    // Um relato com marcas vazias ainda carrega rotulo, URL, o diario do laco e
    // o censo WebGL — que e justamente o que se precisa saber num travamento.
    const semMarcas = Object.keys(marcas).length === 0;
    const corpo = JSON.stringify({
      tipo: 'perf-marcas',
      faseAnterior: rotulo,
      urlAnterior: window.location.pathname + window.location.search,
      ua: navigator.userAgent,
      // `anexo` existe porque o unico canal de diagnostico que funciona no
      // aparelho do comprador e este. Num iPhone dentro do iframe da loja nao
      // ha console para ler `window.__turbineArDebug` — pedir isso foi um erro
      // meu. O que precisa ser visto tem que viajar junto com as marcas.
      dados: {
        marcas,
        // Explicito, para o leitor do log nao confundir "sem marcas" com falha
        // de serializacao.
        ...(semMarcas ? { semMarcas: true } : {}),
        agora: Math.round(performance.now()),
        // Por que o primeiro frame demorou (ver o diario em renderLoop.ts).
        laco: (window as unknown as { __turbineLaco?: unknown }).__turbineLaco ?? null,
        // Contexto WebGL vivo NESTE realm, em TODO relato — nao so quando ha
        // queda. O que o iOS derruba e o processo da ABA, que hospeda a PDP +
        // o iframe da Galeria + o iframe do Personalizador; o censo
        // (lib/perf/webglCensus.ts) so ve o proprio realm, e o comentario de la
        // registra que o total simultaneo nunca foi medido, era hipotese.
        // Como os dois documentos reportam, o total da aba sai por soma no log.
        censo: censoResumido(),
        ...(anexo ? { anexo } : {}),
      },
    });
    const url = '/api/public/client-probe';
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([corpo], { type: 'application/json' }));
    } else {
      void fetch(url, { method: 'POST', body: corpo, keepalive: true,
        headers: { 'Content-Type': 'application/json' } });
    }
  } catch {
    // telemetria nunca pode afetar a pagina
  }
}
